export const unknownAnswer = "I don't know yet";

export const activityContextFields = [
  { key: "role", label: "Who performs this subtask?", example: "Accounts payable clerk" },
  { key: "trigger", label: "What starts it?", example: "An invoice arrives in the inbox" },
  { key: "tools", label: "Which applications are used?", example: "SAP and Outlook" },
  { key: "outcome", label: "When is it finished?", example: "The checked invoice is released for payment" },
  { key: "variants", label: "What exceptions or handoffs are outside this subtask?", example: "Discrepancies go to a separate clarification subtask; or enter None" },
];

export const activityFitQuestions = [
  { id: "digital", question: "Is the work mainly digital?", options: ["Mainly digital", "Partly digital", "Mainly offline"] },
  { id: "continuity", question: "Can one person complete it in one continuous work session?", options: ["Yes", "Partly, with handoffs or waiting", "No"] },
  { id: "frequency", question: "How often is it performed?", options: ["Daily", "Weekly", "Monthly", "Yearly or less often"] },
  { id: "standardization", question: "How consistent is the work across executions?", options: ["Mostly consistent", "Some variation", "Highly variable"] },
];

// Die Fragen werden im Modal einfacher gestellt. Die gespeicherten Werte bleiben die der Umfrage
const reviewQuestionTexts = {
  digital: {
    question: "Is the work done on a computer?",
    labels: { "Mainly digital": "Yes", "Partly digital": "Partly", "Mainly offline": "No" },
  },
  continuity: {
    question: "Can one person do it without handoffs or waiting for others?",
    options: ["Yes", "Partly, with handoffs or waiting"],
    labels: { "Partly, with handoffs or waiting": "No" },
  },
  frequency: {
    labels: { "Yearly or less often": "Yearly or less" },
  },
};

// Die Konsistenz-Frage bleibt nur in der Team-Umfrage, im Review-Modal entscheidet sie nichts
export const reviewFitQuestions = activityFitQuestions
  .filter((question) => question.id !== "standardization")
  .map((question) => ({ ...question, ...reviewQuestionTexts[question.id] }));

export function activityPath(row) {
  return JSON.stringify(["l1", "l2", "l3", "l4"].map((level) => String(row[level] || "").trim().toLocaleLowerCase()));
}

export function mergeActivityReviews(framework, incoming = [], previous = []) {
  const oldByPath = new Map(previous.map((row) => [activityPath(row), row]));
  const incomingByPath = new Map(incoming.map((row) => [activityPath(row), row]));
  return framework.flatMap((l1) => (l1.l2 || []).flatMap((l2) => (l2.l3 || []).flatMap((l3) => (l3.l4 || []).map((l4) => {
    const path = { l1: l1.name, l2: l2.name, l3: l3.name, l4 };
    const key = activityPath(path);
    const old = oldByPath.get(key);
    return { ...incomingByPath.get(key), ...(old?.assessmentSource === "user_reviewed" ? old : {}), ...path,
      context: old?.context || {}, included: old?.included ?? incomingByPath.get(key)?.status !== "not_suitable" };
  }))));
}

export function assessActivity(answers = {}) {
  if (answers.digital === "Mainly offline") return { status: "not_suitable", reason: "Mainly offline work is not suitable for Task Mining. Do not include this subtask in the framework." };
  if (answers.frequency === "Yearly or less often") return { status: "not_suitable", reason: "Work that happens yearly or less often does not produce enough data during a Task Mining project. Do not include this subtask in the framework." };
  if (answers.continuity === "No" || answers.continuity === "Partly, with handoffs or waiting") return { status: "not_suitable", reason: "This subtask includes handoffs or waiting and is not suitable as one Task Mining unit. Split it into smaller subtasks and assess them separately before including them in the framework." };
  if (answers.frequency === "Monthly") return { status: "needs_clarification", reason: "Monthly work may only occur a few times during the Task Mining project. Check whether enough executions will occur before including this subtask in the framework." };
  if (answers.digital === "Partly digital") return { status: "needs_clarification", reason: "Data can only be collected for the digital part. Describe that part as its own subtask or rename this subtask to cover only the digital steps." };
  if (reviewFitQuestions.some((question) => answers[question.id] === unknownAnswer)) return { status: "needs_clarification", reason: "Some answers are still unclear. Clarify them before including this subtask in the framework." };
  if (!reviewFitQuestions.every((question) => question.options.includes(answers[question.id]))) return { status: "needs_clarification", reason: "Answer all suitability questions before deciding whether to include this subtask in the framework." };
  return { status: "suitable", reason: "Your answers indicate that this subtask is suitable for Task Mining. Data collection still needs to be set up." };
}

export function activityOpenItems(row) {
  const missing = ["l1", "l2", "l3", "l4"].filter((level) => !String(row[level] || "").trim() || row[level] === "—").map((level) => `Complete ${level.toUpperCase()}`);
  if (row.fitStatus !== "suitable") missing.push(row.fitReason || "Review Task Mining suitability");
  return missing;
}
